import React from 'react';
import {BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip,} from 'chart.js';
import {Box, Container, Typography,} from '@mui/material';
import './CorporateRisk.css';
import TextCard from "./TextCard";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {Bar} from "react-chartjs-2";

// ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title, ChartDataLabels);

const CorporateExposureSummary = (company, chartRef, isLoading) => {
    const corporateRisk = corporateExposureParser(company, chartRef);
    return (
        <div id="content" className={"bg-black"}
             style={{
                 width: '100%', margin: "10px auto 0 auto", padding: "0 15px", fontSize: "1rem", fontWeight: "500",
                 lineHeight: "1.5", height: "20%"
             }}>
            {isLoading && (<Typography variant="h5" className="gray-text">Loading...</Typography>)}
            {!isLoading && (corporateRisk)}
        </div>
    );
}

const corporateExposureParser = (company, chartRef) => {
    return (
        <>
            <table className={"table table-dark"} style={{backgroundColor: "black"}}>
                <tbody>
                <tr>
                    <td width={'25%'} style={{verticalAlign: "middle"}}>
                        <Typography variant="h4" className="gray-text">Exposure Summary</Typography>
                    </td>
                    <td width={'75%'}>
                        <TextCard content={`${company.exposure_summary}`}/>
                    </td>
                </tr>
                </tbody>
            </table>
            <table className={"table table-dark"} style={{backgroundColor: "black"}}>
                <tbody>
                <tr>
                    <td>
                        <TextCard
                            content={`Distribution of physical risk across ${company.ticker} assets for acute hazards (e.g., cyclone, wildfire, flood)`}/>
                    </td>
                    <td>
                        <TextCard
                            content={`Distribution exposure ratings across the portfolio broken down by both acute and chronic hazards`}/>
                    </td>
                </tr>
                <tr>
                    <td>
                        {BarChart(company)}
                    </td>
                    <td>
                        {StackedBarChart(company)}
                    </td>
                </tr>
                </tbody>
            </table>
        </>
    )
}

const BarChart = (company) => {
    const overallBarData = {
        labels: ['LOW', 'MEDIUM', 'HIGH'],
        datasets: [
            {
                label: 'Overall Risk Category',
                data: [
                    company.percentage_assets_risk_category_overall.LOW,
                    company.percentage_assets_risk_category_overall.MEDIUM,
                    company.percentage_assets_risk_category_overall.HIGH,
                ],
                backgroundColor: ['#4caf50', '#ff9800', '#f44336'],
            },
        ],
    };

    const overallOptions = {
        responsive: true,
        aspectRatio: 1.5,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            datalabels: {
                color: 'white',
                font: {
                    size: 18,
                    weight: 'bold',
                },
                formatter: (value) => `${(value * 100).toFixed(0)}%`,
                anchor: 'center',
                align: 'center',
            },
        },
        scales: {
            y: {
                ticks: {
                    display: false,
                },
                grid: {
                    display: false,
                },
                title: {
                    display: true,
                    text: 'Assets in Risk Category',
                    padding: 10,
                    font: {
                        size: 20,
                        weight: 'bold',
                    },
                },
                max: 1,
            },
            x: {
                ticks: {
                    font: {
                        size: 18,
                        weight: 'bold',
                    },
                },
                grid: {
                    display: false,
                },
            },
        },
    };

    return (
        <Container className={'custom-card'} sx={{ py: 4 }}>
            <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', mb: 5 }}>
                <Typography variant="h5" gutterBottom>
                    Overall Asset Risk
                </Typography>
                <Bar data={overallBarData} options={overallOptions} plugins={[ChartDataLabels]}/>
            </Box>
        </Container>
    );
}

const StackedBarChart = (company) => {
    const hazards = ['cyclone', 'flood', 'heatwave', 'water_stress', 'sea_level_rise', 'wildfire'];
    const stackedBarData = {
        labels: hazards.map((h) => h.replace(/_/g, ' ').toUpperCase()),
        datasets: [
            {
                label: 'LOW',
                backgroundColor: '#4caf50',
                data: hazards.map((h) => company[`percentage_assets_risk_category_${h}`].LOW),
            },
            {
                label: 'MEDIUM',
                backgroundColor: '#ff9800',
                data: hazards.map((h) => company[`percentage_assets_risk_category_${h}`].MEDIUM),
            },
            {
                label: 'HIGH',
                backgroundColor: '#f44336',
                data: hazards.map((h) => company[`percentage_assets_risk_category_${h}`].HIGH),
            },
        ],
    };
    const stackedOptions = {
        responsive: true,
        aspectRatio: 1.6,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: false,
            },
            datalabels: {
                color: 'white',
                font: {
                    size: 15,
                    weight: 'bold',
                },
                formatter: (value) => `${(value * 100).toFixed(0)}%`,
                anchor: 'center',
                align: 'center',
            },
        },
        scales: {
            y: {
                stacked: true,
                ticks: {
                    display: false,
                },
                grid: {
                    display: false,
                },
                title: {
                    display: true,
                    text: 'Assets in Risk Category',
                    padding: 10,
                    font: {
                        size: 20,
                        weight: 'bold',
                    },
                },
                max: 1,
            },
            x: {
                stacked: true,
                ticks: {
                    font: {
                        size: 12,
                        weight: 'bold',
                    },
                },
                grid: {
                    display: false,
                },
            },
        },
    };

    return (
        <Container className={'custom-card'} sx={{ py: 4 }}>
            <Box sx={{ width: '100%', maxWidth: 700, mx: 'auto' }}>
                <Typography variant="h5" gutterBottom>
                    Asset Risk by Hazard
                </Typography>
                <Bar data={stackedBarData} options={stackedOptions} plugins={[ChartDataLabels]}/>
            </Box>
        </Container>
    );
}

export default CorporateExposureSummary;
